import React from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { translateUnabbreviatedTimezone } from "@qgiv/core-js";
import { AddToCalendar } from "@qgiv/core-donor";
import { selectCmsControlById } from "../../../../redux/slices/cmsSettingsSlice";
import { selectReceipt } from "../../../../redux/slices/receiptSlice";
import AddToCalendarCmsMessage from "./AddToCalendarCmsMessage";
import {
    selectIsCms,
    selectConfig,
} from "../../../../redux/slices/configSlice";
import { defaultCalendarData } from "../../../../redux/utilities/defaultData/defaultRegistrationData";

/**
 * @param {object} props
 * @param {string} props.controlId
 * @returns {React.ReactElement|null}
 */
const ConnectedAddToCalendar = ({ controlId }) => {
    const control = useSelector((state) =>
        selectCmsControlById(controlId, state),
    );
    const receipt = useSelector(selectReceipt);
    const { transaction = {} } = receipt;
    const { calendarData = {} } = transaction;
    const config = useSelector(selectConfig);

    // Determines whether calendar data is available
    // based on the campaign or classification start dates
    // configured by org in control panel.
    const { hasStartDateForCalendar = false } = config;

    const {
        options: {
            heading = "Keep us on your schedule!",
            additionalText = "Need an event reminder? Mark the date on your calendar.",
            buttonLabel = "Add to Calendar",
            addToCalendarButtonStyle = 2,
        },
    } = control;

    const isCms = useSelector(selectIsCms);
    const disabled = isCms;

    const {
        address = "",
        city = "",
        e_date_begin = "",
        e_date_end = "",
        e_time_begin = "",
        e_time_end = "",
        // The BE ensures that 'description' is stripped of HTML tags.
        description = "",
        name = "",
        state = "",
        timezone = 1,
        zip = "",
    } = isCms ? defaultCalendarData : calendarData;

    // Use "MM/DD/YYYY" to avoid moment.js deprecation warnings for non-ISO formats.
    const eventStartDate = moment(e_date_begin, "MM/DD/YYYY");
    const eventEndDate = moment(e_date_end, "MM/DD/YYYY");

    const hasValidStartAndEndDate =
        eventStartDate.isValid() && eventEndDate.isValid();

    let startDate = "";
    let endDate = "";

    // Format the start and end dates as strings compatible with the
    // getFormattedDateString function, if the dates are valid.
    if (hasValidStartAndEndDate) {
        startDate = `${eventStartDate.format("YYYY-MM-DD")} ${e_time_begin}`;
        endDate = `${eventEndDate.format("YYYY-MM-DD")} ${e_time_end}`;
    }

    const formatAddress = () => {
        const stateZip = [state, zip].filter(Boolean).join(" ");
        const _formattedAddress = [address, city, stateZip]
            .filter(Boolean)
            .join(", ");
        return _formattedAddress;
    };

    const formattedAddress = formatAddress();

    const eventData = {
        startDate,
        endDate,
        timezone: translateUnabbreviatedTimezone(timezone),
        title: name,
        description,
        // P2P event data does not include a 'location' field.
        // Adding it here ensures compatibility with the eventData format
        // required by the add_to_calendar macro.
        location: "",
        formattedAddress,
    };

    const buttonStyleSettings = {
        buttonLabel,
        // Convert addToCalendarButtonStyle to a number to ensure proper type handling
        btnStyle:
            Number(addToCalendarButtonStyle) === 2 ? "secondary" : "primary",
    };

    const addToCalendarProps = {
        heading,
        additionalText,
        disabled,
        ...eventData,
        ...buttonStyleSettings,
    };

    const isCmsWithStartDate = isCms && hasStartDateForCalendar;
    const isCmsWithoutStartDate = isCms && !hasStartDateForCalendar;
    const eventStartDateExists = !!e_date_begin;

    if (eventStartDateExists || isCmsWithStartDate) {
        return <AddToCalendar {...addToCalendarProps} />;
    }
    if (isCmsWithoutStartDate) {
        return <AddToCalendarCmsMessage />;
    }
    return null;
};

export default ConnectedAddToCalendar;
